import { Language } from "@medapro-types/common/common";

export enum TranslationGroupType {
  ArticleDescriptions = "articleDescriptions",
  ArticleNames = "articleNames",
  AttributeValues = "attributeValues",
  AttributeUnits = "attributeUnits",
  CatalogGroupAttributes = "catalogGroupAttributes",
  CatalogGroupDescriptions = "catalogGroupDescriptions",
  CatalogGroupNames = "catalogGroupNames",
  GlobalAttributeUnits = "globalAttributeUnits",
  GlobalAttributeValues = "globalAttributeValues",
  Keywords = "keywords",
  VariationGroups = "variationGroups"
}

export enum TranslationEntryValueFilterType {
  All = "all",
  Empty = "empty",
  Multiple = "multiple",
  OverLong = "overLong"
}

export enum TranslationGroupSortColumn {
  Count = "count",
  SourceText = "sourceText"
}

export interface TranslationProject {
  translationProjectId: string;
  name: string;
  catalog: Catalog;
  sourceLanguage: Language | null;
  targetLanguage: Language | null;
  updatedOn?: Date | null;
  updatedBy?: string | null;
  createdOn: Date | null;
  createdBy: string | null;
  groups: TranslationGroupSummary[];
}

export interface TranslationGroupSummary {
  type: TranslationGroupType;
  count: number;
}

export interface TranslationEntry {
  sourceText: string;
  targetText?: string | null;
}

export interface TranslationDetailEntry extends TranslationEntry {
  referenceName: string;
  referenceNo: string;
  referenceId: string;
  attributeId: string;
  originalTargetText?: string | null;
}

export interface TranslationGroupEntry extends TranslationEntry {
  count: number;
  hasDifferingValues: boolean;
  detailEntries: TranslationDetailEntry[];
  originalTargetText?: string | null;
}

export interface TranslationGroup {
  type: TranslationGroupType;
  entries: TranslationGroupEntry[];
  searchParameters: SearchParameterState | null;
  initialHash: string | null;
  count: number;
}

export interface SearchParameterState {
  currentPage: number;
  entriesPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  orderByColumn?: TranslationGroupSortColumn | null;
  orderDescending?: boolean;
  searchText: string | null;
  entryValueFilterType: TranslationEntryValueFilterType;
  isLoading?: boolean;
}

export type CreateTranslationProjectData = {
  projectName: string | null;
  catalogId: string | null;
  sourceLanguageId: number | null;
  targetLanguageId: number | null;
  groups: TranslationGroupCreateData[];
  showOnlyOutdated: boolean;
};

export type TranslationGroupCreateData = {
  type: TranslationGroupType;
  enabled: boolean;
  entryCount: number;
};

export type Catalog = {
  catalogId: string;
  name: string | null;
  fallbackName: string | null;
};

export type TranslationGroupSearchParameters = {
  translationProjectId: string;
  groupType: TranslationGroupType;
  searchText: string | null;
  entryValueFilterType: TranslationEntryValueFilterType;
  pageNumber: number;
  entriesPerPage: number;
  orderByColumn?: TranslationGroupSortColumn | null;
  orderDirection?: boolean;
  skipLoadingIndicatorControl?: boolean | null;
};

export type TranslationGroupSearchResult = {
  entries: TranslationGroupEntry[];
  searchParameters: SearchParameterState;
};

export type TranslationDetailSearchParameters = {
  translationProjectId: string;
  groupType: TranslationGroupType;
  sourceText: string;
  languageCode: string;
};

export interface Selectable {
  selected: boolean;
}

export enum TranslationFileType {
  Excel = "excel",
  XLIFF = "xliff"
}

export interface TranslationManagerOverviewValues {
  textCount: number;
  emptyTextCount: number;
  translatedTextCount: number;
  wordCount: number;
  emptyWordCount: number;
  translatedWordCount: number;
  characterCount: number;
  emptyCharacterCount: number;
  translatedCharacterCount: number;
}

export const DEFAULT_PAGE_SIZE: number = 250;
